import React, { ButtonHTMLAttributes } from "react";

export type ButtonVariant =
	| "primary"
	| "secondary"
	| "outline"
	| "danger"
	| "icon";
export type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	icon?: React.ReactNode;
	fullWidth?: boolean;
	children?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
	variant = "primary",
	size = "md",
	icon,
	fullWidth = false,
	children,
	className = "",
	disabled,
	...rest
}) => {
	// Base classes
	const baseClasses =
		"rounded font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2";

	// Size classes
	const sizeClasses = {
		sm: "px-2 py-1 text-sm",
		md: "px-4 py-2",
		lg: "px-6 py-3 text-lg",
	};

	// Variant classes
	const variantClasses = {
		primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
		secondary:
			"bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500",
		outline:
			"border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-blue-500",
		danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500",
		icon: "bg-transparent hover:bg-gray-100 p-2 focus:ring-gray-500",
	};

	// Disabled classes
	const disabledClasses = disabled
		? "opacity-50 cursor-not-allowed"
		: "cursor-pointer";

	// Width classes
	const widthClasses = fullWidth ? "w-full" : "";

	// Combine all classes
	const buttonClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${disabledClasses}
    ${widthClasses}
    ${className}
  `.trim();

	return (
		<button className={buttonClasses} disabled={disabled} {...rest}>
			{icon && (
				<span className={children ? "mr-2 inline-flex" : "inline-flex"}>
					{icon}
				</span>
			)}
			{children}
		</button>
	);
};

export default Button;
